
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SearchTradingAccounts } from '@/components/profile/SearchTradingAccounts';
import { useTradingAccountValidation } from '@/hooks/use-trading-account-validation';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { Plus } from 'lucide-react';

interface SearchConnectSectionProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onOpenConnectDialog: () => void;
}

export function SearchConnectSection({ 
  searchQuery, 
  onSearchChange,
  onOpenConnectDialog
}: SearchConnectSectionProps) {
  const { t } = useTranslation();
  const { canCreateAccount, getErrorMessage, currentCount, maxAccounts } = useTradingAccountValidation();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleConnectButtonClick = () => {
    if (!canCreateAccount()) {
      toast({
        title: t('account_limit_reached'),
        description: getErrorMessage(),
        variant: "destructive"
      });
      return;
    }
    onOpenConnectDialog();
  };

  return (
    <Card className="bg-gradient-to-r from-background to-muted/20 border-border/50 shadow-sm">
      <div className={`${isMobile ? 'p-3' : 'p-4 md:p-6'}`}>
        <div 
          className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-col md:flex-row md:items-center md:gap-4 gap-3'}`}
          data-tour="search-accounts"
        >
          <div className="flex-1">
            <SearchTradingAccounts 
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
            />
          </div>
          <div className={`flex-none flex items-center ${isMobile ? 'justify-stretch' : 'justify-end'}`}>
            <Button 
              onClick={handleConnectButtonClick} 
              disabled={!canCreateAccount()}
              data-tour="connect-dialog"
              size={isMobile ? "sm" : "default"}
              className={`group bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] ${isMobile ? 'w-full h-9' : ''}`}
            >
              <Plus className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'} transition-transform duration-200 group-hover:rotate-90`} />
              <span className={`font-medium ${isMobile ? 'text-sm' : ''}`}>
                {isMobile ? 'Connect Account' : 'Connect Account'}
              </span>
              {!isMobile && (
                <span className="ml-2 text-xs opacity-75">
                  ({currentCount}/{maxAccounts})
                </span>
              )}
              {isMobile && (
                <span className="ml-1 text-xs opacity-75">
                  ({currentCount}/{maxAccounts})
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
